<template>
  <PageTemplate
    heading="Påløpt, ubetalt produktforbruk"
    :loading="isLoading"
    :showError="hasError"
    errorMessage="Kunne ikke laste produktforbruk"
  >
    <InfoCard>
      <div>
        Her finner du en oversikt over produkter du har kjøpt eller bestilt, men som fortsatt står ubetalt.
        Vi trekker beløpene sammen med neste utbetaling av husleie. Har du valgt faktura som betalingsmetode,
        sender vi den til deg i stedet.
        Vær oppmerksom på at Premium-abonnementet er delt i to linjer, siden delene har forskjellig mva-behandling.
      </div>
    </InfoCard>

    <BlockContainer v-if="!isLoading && hasUninvoicedFees" class="mt-4 p-5">
      <!-- Summary Section -->
      <div class="summary-section">
        <div class="summary-card">
          <h4>Påløpt, ubetalt beløp</h4>
          <p class="summary-value">
            <Currency :amount="totalAmount" />
          </p>
        </div>
        <div class="summary-card">
          <h4>Antall produkter</h4>
          <p class="summary-value">{{ totalCount }}</p>
        </div>
      </div>

      <!-- Filter Row -->
      <form class="w-full max-w-lg">
        <div class="filter-row">
          <div class="filter-utility">
            <i class="budicon-filter mr-2"></i>
          </div>
          <div>
            <label>Utleier</label>
            <select name="landlord" v-model="selectedLandlord">
              <option value="">-- Alle --</option>
              <option v-for="option in landlordOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Leieobjekt</label>
            <select name="rentalObjects" v-model="selectedRentalObject">
              <option value="">-- Alle --</option>
              <option v-for="option in rentalObjectOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Produkt</label>
            <select name="product" v-model="selectedProduct">
              <option value="">-- Alle --</option>
              <option v-for="option in productOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Tittel/Leietaker</label>
            <input name="title" v-model="searchTitle" />
          </div>
          <div>
            <label>Periode fra og med</label>
            <select name="periodFrom" v-model="selectedPeriodFrom">
              <option value="">-- Alle --</option>
              <option v-for="option in periodOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div>
            <label>Periode til og med</label>
            <select name="periodTo" v-model="selectedPeriodTo">
              <option value="">-- Alle --</option>
              <option v-for="option in periodOptions" :key="option.value" :value="option.value">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div class="filter-utility">
            <button class="btn btn-outline-secondary btn-sm" @click="clearFilters" type="button" title="Tøm filter">
              <i class="budicon-eraser text-black"></i>
            </button>
          </div>
        </div>
      </form>

      <!-- Flat Table -->
      <table>
        <thead>
          <tr>
            <th>Utleier</th>
            <th>Adresse</th>
            <th>Leietaker</th>
            <th>Produkttype</th>
            <th>Tittel</th>
            <th>Periode</th>
            <th class="text-right">Beløp</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in filteredRows" :key="index">
            <td>{{ row.landlordName }}</td>
            <td>{{ row.rentalObjectStreet || '-' }}</td>
            <td>{{ row.tenantName || '-' }}</td>
            <td>{{ row.productName || '-' }}</td>
            <td>{{ row.title || '-' }}</td>
            <td>{{ formatPeriod(row.period) }}</td>
            <td class="text-right font-mono">
              <Currency :amount="row.price || 0" />
            </td>
          </tr>
        </tbody>
      </table>
    </BlockContainer>

    <BlockContainer v-else-if="!isLoading && !hasError && !hasUninvoicedFees" class="mt-4 p-5">
      <p class="no-data">Ingen produktforbruk funnet</p>
    </BlockContainer>
  </PageTemplate>
</template>

<script>
import PageTemplate from '@/components/PageTemplate.vue';
import BlockContainer from '@/components/BlockContainer.vue';
import InfoCard from '@/components/InfoCard.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import { ProfilesLandlordApi } from '@modules/husleieapi2';
import { mapGetters } from 'vuex';

export default {
  name: 'UninvoicedFeesDetails',
  components: {
    PageTemplate,
    BlockContainer,
    InfoCard,
    Currency,
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      uninvoicedFeesData: null,
      selectedPeriodFrom: '',
      selectedPeriodTo: '',
      selectedRentalObject: '',
      selectedProduct: '',
      searchTitle: '',
      selectedLandlord: '',
    };
  },
  computed: {
    ...mapGetters({
      landlordProfiles: 'getLandlordProfilesMeta',
    }),
    // Flatten all rows from all landlords into a single array
    flattenedRows() {
      if (!this.uninvoicedFeesData || this.uninvoicedFeesData.length === 0) {
        return [];
      }

      const rows = [];
      this.uninvoicedFeesData.forEach(landlordData => {
        if (landlordData.rows && landlordData.rows.length > 0) {
          landlordData.rows.forEach(row => {
            rows.push({
              ...row,
              landlordId: landlordData.landlordId,
              landlordName: landlordData.landlordName || this.getLandlordName(landlordData.landlordId),
            });
          });
        }
      });

      return rows;
    },
    hasUninvoicedFees() {
      return this.flattenedRows.length > 0;
    },
    totalAmount() {
      if (!this.uninvoicedFeesData) return 0;

      return this.uninvoicedFeesData.reduce((total, landlordData) => {
        return total + (landlordData.totalAmount || 0);
      }, 0);
    },
    totalCount() {
      if (!this.uninvoicedFeesData) return 0;

      return this.uninvoicedFeesData.reduce((total, landlordData) => {
        return total + (landlordData.totalRows || 0);
      }, 0);
    },
    // Extract unique periods for filter dropdown
    periodOptions() {
      const periods = new Set();
      this.flattenedRows.forEach(row => {
        if (row.period) {
          periods.add(row.period);
        }
      });

      return Array.from(periods)
        .sort()
        .map(period => ({
          name: this.formatPeriod(period),
          value: period,
        }));
    },
    // Extract unique rental objects for filter dropdown
    rentalObjectOptions() {
      const rentalObjects = new Set();
      this.flattenedRows.forEach(row => {
        if (row.rentalObjectId) {
          rentalObjects.add(JSON.stringify({ id: row.rentalObjectId, name: row.rentalObjectStreet || '-' }));
        }
      });

      return Array.from(rentalObjects)
        .map(item => JSON.parse(item))
        .map(obj => ({
          name: obj.name,
          value: obj.id,
        }));
    },
    // Extract unique products for filter dropdown
    productOptions() {
      const products = new Set();
      this.flattenedRows.forEach(row => {
        if (row.productName) {
          products.add(row.productName);
        }
      });

      return Array.from(products)
        .sort()
        .map(product => ({
          name: product,
          value: product,
        }));
    },
    // Extract unique landlords for filter dropdown
    landlordOptions() {
      const landlords = new Set();
      this.flattenedRows.forEach(row => {
        if (row.landlordId) {
          landlords.add(JSON.stringify({ id: row.landlordId, name: row.landlordName }));
        }
      });

      return Array.from(landlords)
        .map(item => JSON.parse(item))
        .map(obj => ({
          name: obj.name,
          value: obj.id,
        }));
    },
    // Filter rows based on all filter criteria
    filteredRows() {
      return this.flattenedRows.filter(row => {
        let include = true;

        // Period filter (from)
        if (this.selectedPeriodFrom && row.period && row.period < this.selectedPeriodFrom) {
          include = false;
        }

        // Period filter (to)
        if (this.selectedPeriodTo && row.period && row.period > this.selectedPeriodTo) {
          include = false;
        }

        // Rental object filter
        if (this.selectedRentalObject && row.rentalObjectId !== this.selectedRentalObject) {
          include = false;
        }

        // Product filter
        if (this.selectedProduct && row.productName !== this.selectedProduct) {
          include = false;
        }

        // Title search filter
        if (this.searchTitle && row.title) {
          if (!row.title.toLowerCase().includes(this.searchTitle.toLowerCase())) {
            include = false;
          }
        }

        // Landlord filter
        if (this.selectedLandlord && row.landlordId !== this.selectedLandlord) {
          include = false;
        }

        return include;
      });
    },
  },
  methods: {
    async fetchUninvoicedFees() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const api = new ProfilesLandlordApi();
        const response = await api.getProfileLandlordsUninvoicedFees();

        this.uninvoicedFeesData = response;
      } catch (error) {
        // Error fetching uninvoiced fees
        this.hasError = true;
        this.$toast('Kunne ikke laste produktforbruk', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    getLandlordName(landlordId) {
      const landlord = this.landlordProfiles.find(ll => ll.id === landlordId);
      return landlord ? landlord.name : `Landlord ${landlordId}`;
    },
    formatPeriod(period) {
      if (!period) return '-';

      // If it's an ISO date string (YYYY-MM-DD or YYYY-MM)
      if (typeof period === 'string' && period.match(/^\d{4}-\d{2}/)) {
        const [year, month] = period.split('-');
        const months = ['', 'jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
        return `${months[parseInt(month, 10)]} ${year}`;
      }

      return period;
    },
    clearFilters() {
      this.selectedPeriodFrom = '';
      this.selectedPeriodTo = '';
      this.selectedRentalObject = '';
      this.selectedProduct = '';
      this.searchTitle = '';
      this.selectedLandlord = '';
    },
  },
  mounted() {
    this.fetchUninvoicedFees();
  },
};
</script>

<style scoped>
.summary-section {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4 mb-6;
}

.summary-card {
  @apply bg-gray-lighter border border-gray-light rounded-lg p-4 text-center;
}

.summary-card h4 {
  @apply text-sm font-medium text-gray-darker mb-2;
}

.summary-value {
  @apply text-2xl font-bold text-black m-0;
}

.filter-row {
  @apply flex flex-auto -mx-3 mb-2 mt-5;
}

.filter-row > div {
  @apply w-full md:w-1/6 px-3 mb-6 md:mb-0;
}

.filter-row > div > label {
  @apply block uppercase tracking-wide text-[rgb(102,102,102)] text-xs font-bold mt-2;
}

.filter-row > div > input,
.filter-row > div > select {
  @apply appearance-none block w-full border rounded py-2 px-2 mb-3 leading-tight focus:outline-none focus:bg-white;
}

.filter-utility {
  width: 16px !important;
  margin: 36px 14px 0 0;
  @apply text-[1.2em];
}

table {
  @apply w-full mt-5 table-auto border-collapse text-[0.9em] shadow-md;
}

table thead tr {
  @apply bg-[rgb(224,224,224)] text-black text-left;
}

table th,
table td {
  @apply py-2 px-3;
}

table tbody tr {
  border-bottom: 1px solid #dddddd;
}

table tbody tr:nth-of-type(even) {
  background-color: #f3f3f3;
}

table tbody tr:last-of-type {
  border-bottom: 2px solid #dddddd;
}

.font-mono {
  font-family: monospace !important;
}

.no-data {
  @apply text-center text-gray-dark py-4 m-0;
}
</style>
